import React, { useState, useEffect, useContext, useRef } from "react";
import { motion } from "framer-motion";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import axios from "axios";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import { SidebarContext } from "./SidebarContext";
import Sidebar from "./Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import TransactionProofImage from "./TransactionProofImage";
import TransactionImageField from "./TransactionImageField";
import { fetchTransactionPhotoRequired, imagePayload } from "../utils/transactionPhoto";
import { formatKolkataDateTime } from "../utils/dateFormat";

const AdminRequestReport = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejectReason, setRejectReason] = useState({});
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({});
  const [approvalImages, setApprovalImages] = useState({});
  const [photoRequired, setPhotoRequired] = useState(false);
  const { isSidebarOpen = false, screenWidth = 0 } = useContext(SidebarContext) || {};
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = localStorage.getItem("role") || "";
  const userId = localStorage.getItem("id");

  useEffect(() => {
    if (!userId || userRole !== "company_owner") {
      setError("Access denied. Only company owner can view this page.");
      toast.error("Access denied");
      navigate("/");
      return;
    }
    fetchRequests();
    fetchTransactionPhotoRequired(userId).then(setPhotoRequired).catch(() => {});
  }, []);

  useEffect(() => {
    const filtered = requests.filter(
      (request) =>
        (request.admin_name || request.admin_user_name || "")
          .toLowerCase()
          .includes(searchName.toLowerCase()) &&
        (request.created_at || "").includes(searchDate)
    );
    setFilteredRequests(filtered);
  }, [searchName, searchDate, requests]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${ApiURL}/getAdminRequests.php`, {
        params: { user_id: userId, role: userRole },
      });
      if (response.data.status === "success") {
        setRequests(response.data.data || []);
        setFilteredRequests(response.data.data || []);
      } else {
        setError("Failed to fetch requests");
      }
    } catch (error) {
      setError(`Network error: ${error.message}`);
      toast.error(`Error fetching requests: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (requestId, status) => {
    setSelectedStatus((prev) => ({ ...prev, [requestId]: status }));
    setSelectedRequestId(requestId);
  };

  const handleRejectReasonChange = (requestId, reason) => {
    setRejectReason((prev) => ({ ...prev, [requestId]: reason }));
  };

 const handleAction = async (requestId) => {
  const action = selectedStatus[requestId];
  const reason = rejectReason[requestId] || "";

  if (!action) {
    setError("Please select an action");
    toast.error("Please select an action");
    return;
  }
  if (action === "reject" && !reason.trim()) {
    setError("Please provide a reason for rejection");
    toast.error("Please provide a reason for rejection");
    return;
  }

  if (action === "approve") {
    const request = requests.find((r) => r.id === requestId);
    if (photoRequired && !approvalImages[requestId]?.dataUrl && !request?.attachment_path) {
      setError("Approval proof image is required");
      toast.error("Approval proof image is required");
      return;
    }
    const confirmApproval = window.confirm(
      `Are you sure you want to approve this request? ${formatCurrency(request?.amount || 0)} will be deducted from your balance and credited to ${request?.admin_name || "the admin"}.`
    );
    if (!confirmApproval) {
      toast.info("Approval cancelled");
      return;
    }
  }

  const payload = {
    request_id: Number(requestId),
    action: action,
    reason: action === "reject" ? reason : "",
    owner_id: Number(userId),
    attachment_image: action === "approve" ? imagePayload(approvalImages[requestId]) : "",
  };

  console.log("Sending payload:", payload);

  try {
    const response = await axios.post(`${ApiURL}/updateAdminRequest.php`, payload, {
      headers: { 
        "Content-Type": "application/json",
      },
      timeout: 30000, // 30 second timeout
    });
    
    console.log("Response:", response.data);
    
    if (response.data.status === "success") {
      toast.success(response.data.message);
      fetchRequests();
      setRejectReason((prev) => ({ ...prev, [requestId]: "" }));
      setApprovalImages((prev) => ({ ...prev, [requestId]: null }));
      setSelectedRequestId(null);
      setSelectedStatus((prev) => ({ ...prev, [requestId]: "" }));
    } else {
      const errorMsg = response.data.message || "Unknown error occurred";
      setError(errorMsg);
      toast.error(errorMsg);
    }
  } catch (error) {
    console.error("Full error object:", error);
    let errorMessage = "Unknown error occurred";
    
    if (error.response) {
      // Server responded with error status
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
      errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
    } else if (error.request) {
      // Request was made but no response
      console.error("Error request:", error.request);
      errorMessage = "No response from server. Please check your connection.";
    } else {
      // Something else happened
      errorMessage = error.message || "Request failed";
    }
    
    setError(errorMessage);
    toast.error(errorMessage);
  }
};

  const exportToCSV = () => {
    const headers = [
      "Request ID",
      "Admin Name",
      "Purpose",
      "Amount",
      "Project",
      "Status",
      "Created Date",
      "Updated Date",
      "Rejection Reason",
    ];

    const data = filteredRequests.map((request) => ({
      "Request ID": request.id || "",
      "Admin Name": request.admin_name || "",
      Purpose: request.purpose || "",
      Amount: request.amount || "",
      Project: request.project_name || "",
      Status: request.status || "",
      "Created Date": request.created_at || "",
      "Updated Date": request.updated_at || "",
      "Rejection Reason": request.rejection_reason || "",
    }));

    return { headers, data };
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  return (
    <div className="min-h-screen bg-[#f0f2f8] font-sans">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`min-w-0 flex-1 min-h-screen transition-all duration-300 ${
            isSidebarOpen ? "lg:ml-72" : ""
          } ${isSidebarOpen && screenWidth < 640 ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="mx-auto max-w-7xl px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6">
            <div className="mb-5 border-b border-slate-200 pb-4">
              <h1 className="text-lg font-bold leading-tight text-slate-900 sm:text-xl">Admin Fund Requests</h1>
              <p className="mt-1 text-xs text-slate-500">{filteredRequests.length} requests</p>
            </div>

            <div>
              <form onSubmit={(e) => e.preventDefault()} className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_220px_auto]">
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Search by Admin Name..."
                  className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                />
                <input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  placeholder="Search by Date"
                  className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                />
                <CSVLink
                  data={exportToCSV().data}
                  headers={exportToCSV().headers}
                  filename="AdminRequestReport.csv"
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-red-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Export to CSV
                </CSVLink>
              </form>

              {isLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-500 shadow-sm">Loading requests...</div>
              ) : error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 py-8 text-center text-sm font-medium text-rose-700">Error: {error}</div>
              ) : filteredRequests.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center text-sm text-slate-500 shadow-sm">No pending requests found</div>
              ) : (
                <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                  <table className="min-w-[1180px] w-full text-left text-sm text-slate-700">
                    <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                      <tr>
                        <th className="px-2 py-1.5">Request ID</th>
                        <th className="px-2 py-1.5">Admin Name</th>
                        <th className="px-2 py-1.5">Purpose</th>
                        <th className="px-2 py-1.5">Amount</th>
                        <th className="px-2 py-1.5">Site</th>
                        <th className="px-2 py-1.5">Project</th>
                        <th className="px-2 py-1.5">Main Head</th>
                        <th className="px-2 py-1.5">Sub Head</th>
                        <th className="px-2 py-1.5">Proof</th>
                        <th className="px-2 py-1.5">Created At</th>
                        <th className="px-2 py-1.5">Status</th>
                        <th className="px-2 py-1.5">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request) => (
                        <tr
                          key={request.id}
                          className="border-b border-slate-100 bg-white transition-colors duration-200 hover:bg-slate-50"
                        >
                          <td className="px-2 py-2 font-semibold text-slate-900">{request.id || "-"}</td>
                          <td className="px-2 py-2">{request.admin_name || request.admin_user_name || "-"}</td>
                          <td className="px-2 py-2">{request.purpose || "-"}</td>
                          <td className="px-2 py-2 font-semibold text-slate-900">{formatCurrency(request.amount)}</td>
                          <td className="px-2 py-2">{request.site || "-"}</td>
                          <td className="px-2 py-2">{request.project_name || "-"}</td>
                          <td className="px-2 py-2">{request.main_head || "-"}</td>
                          <td className="px-2 py-2">{request.sub_head || "-"}</td>
                          <td className="px-2 py-1.5"><TransactionProofImage path={request.attachment_path} /></td>
                          <td className="px-2 py-2">{formatKolkataDateTime(request.created_at)}</td>
                          <td className="px-2 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                request.status === "approved"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : request.status === "rejected"
                                  ? "bg-rose-50 text-rose-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              {request.status || "pending"}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            {request.status === "pending" ? (
                              <div className="flex flex-col gap-2">
                                <select
                                  value={selectedStatus[request.id] || ""}
                                  onChange={(e) => handleStatusChange(request.id, e.target.value)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-sm"
                                >
                                  <option value="">Select Action</option>
                                  <option value="approve">Approve</option>
                                  <option value="reject">Reject</option>
                                </select>
                                {selectedStatus[request.id] === "reject" && selectedRequestId === request.id && (
                                  <div className="mt-1">
                                    <input
                                      type="text"
                                      value={rejectReason[request.id] || ""}
                                      onChange={(e) => handleRejectReasonChange(request.id, e.target.value)}
                                      placeholder="Reason for rejection..."
                                      className="w-full rounded-lg border border-rose-200 p-2 text-sm"
                                    />
                                    <button
                                      onClick={() => handleAction(request.id)}
                                      className="mt-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                                    >
                                      Submit
                                    </button>
                                  </div>
                                )}
                                {selectedStatus[request.id] === "approve" && selectedRequestId === request.id && (
                                  <div className="mt-1 min-w-[220px]">
                                    <TransactionImageField
                                      value={approvalImages[request.id] || null}
                                      onChange={(value) => setApprovalImages((prev) => ({ ...prev, [request.id]: value }))}
                                      required={photoRequired && !request.attachment_path}
                                      label="Approval Proof"
                                    />
                                    <button
                                      onClick={() => handleAction(request.id)}
                                      className="mt-2 w-full rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                                    >
                                      Submit
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.main>
      </div>
    </div>
  );
};

export default AdminRequestReport;
